/* eslint-disable no-nested-ternary */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import cx from 'classnames';
import BigNumber from 'bignumber.js';

import Section from 'app/components/common/Section';
import InfoTab from 'app/components/ui/InfoTab';
import Graphic from 'app/components/graphs/Graphic';
import PairInformationTable from 'app/components/tables/PairInformationTable';
import TransactionsTable from 'app/components/tables/TransactionsTable';
import PairHeadSection from 'app/components/ui/PairHeadSection';
import {
  useGetPairPlotLiquidityQuery,
  useGetPairPlotVolumeLazyQuery,
  useGetPairQuery,
  useGetTransactionsByPairLazyQuery,
} from 'app/generated/graphql';
import { PairsType } from 'app/types/head-section';
import {
  getSlice,
  getTokenLogo, prettyPrice, transformMutezToTez,
} from 'app/utils/helpers';
import { InfoTabsDataType } from 'app/types/info-tabs';
import { PooledAssets } from 'app/components/ui/InfoTab/InfoTab';
import { GraphicTypes } from 'app/components/graphs/types';
import { PairInformationDataType } from 'app/types/table';
import { useCurrency } from 'app/context/CurrencyContext';
import { TransactionsScopeEnum } from 'app/types/transactions';
import { useBreadcrumbs } from 'app/context/BreadcrumbsContext';
import NotFound from 'app/pages/not-found';

import s from './PairsDetails.module.sass';

enum SelectGraphicsTypesEnum {
  Liquidity = 'Liquidity',
  Volume = 'Volume',
}

const selectGraphicsTypes = [
  {
    id: 1,
    type: SelectGraphicsTypesEnum.Liquidity,
  },
  {
    id: 2,
    type: SelectGraphicsTypesEnum.Volume,
  },
];

const PairsDetails: React.FC = () => {
  const [
    currentGraphicType,
    setCurrentGraphicType,
  ] = useState<SelectGraphicsTypesEnum>(SelectGraphicsTypesEnum.Liquidity);

  const params: { pair: string } = useParams();
  const location = useLocation();
  const { currency, xtzUsdQuote } = useCurrency();
  const { setBreadcrumbs } = useBreadcrumbs();

  const [fetchCurrentType, { data: transactionData }] = useGetTransactionsByPairLazyQuery();

  const { data, loading, error } = useGetPairQuery(
    {
      variables: {
        id: params.pair,
      },
    },
  );

  const breadcrumbs = useMemo(() => ({
    current: loading ? '...' : error ? 'Not found' : `${data?.pair.token1.symbol ?? 'Unnamed'} / ${getSlice(data?.pair.token2.symbol!) ?? 'Unnamed'}`,
    prev: location.pathname,
  }), [data?.pair.token1.symbol, data?.pair.token2.symbol, error, loading, location.pathname]);

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, [breadcrumbs, setBreadcrumbs]);

  const { data: plotLiquidity, loading: plotLiquidityLoading } = useGetPairPlotLiquidityQuery(
    {
      variables: {
        id: params.pair,
      },
    },
  );

  const [
    getPlotVolume,
    { data: plotVolume, loading: plotVolumeLoading },
  ] = useGetPairPlotVolumeLazyQuery(
    {
      variables: {
        id: params.pair,
      },
    },
  );

  const handleGraphicChange = useCallback((el: SelectGraphicsTypesEnum) => {
    if (el === SelectGraphicsTypesEnum.Volume) {
      getPlotVolume();
    }
    setCurrentGraphicType(el);
  }, [getPlotVolume]);

  const pairHeadSection: PairsType = useMemo(() => ({
    token1OneForOne: data ? prettyPrice(Number(data.pair.token2.price)) : '0',
    token2OneForOne: data ? prettyPrice(1 / Number(data.pair.token2.price)) : '0',
    token1Image: data ? getTokenLogo(data.pair.token1.icon!) : null,
    token2Image: data ? getTokenLogo(data.pair.token2.icon!) : null,
    token1Name: data && data.pair.token1.symbol ? data.pair.token1.symbol : 'Unnamed',
    token1Id: data ? data.pair.token1.id! : 'id',
    token2Name: data && data.pair.token2.symbol ? data?.pair.token2.symbol : 'Unnamed',
    token2Id: data ? data?.pair.token2.id! : 'id',
    token2TokenId: data && data.pair.token2.standard! === 'FA2' ? data?.pair.token2.tokenId! : '',
    token1Price: xtzUsdQuote,
    token2Price: data ? +data.pair.token2.price : 0,
    version: data ? data.pair.version : null,
    token2Standard: data ? data.pair.token2.standard! : 'FA2',
  }), [data, xtzUsdQuote]);

  const infoTabsData = useMemo(() => [
    {
      id: 1,
      title: 'Total liquidity:',
      value: data ? transformMutezToTez({
        value: data.pair.liquidity,
        fraction: 2,
        currency,
        xtzUsdQuote,
      }) : 0,
      change: {
        day: data ? data.pair.liquidityChange.day : 0,
        week: data ? data.pair.liquidityChange.week : 0,
        month: data ? data.pair.liquidityChange.month : 0,
      },
    },
    {
      id: 2,
      title: 'Volume (24h):',
      value: data ? transformMutezToTez({
        value: data.pair.volume24h,
        fraction: 2,
        currency,
        xtzUsdQuote,
      }) : 0,
      change: {
        day: data ? data.pair.volumeChange.day : 0,
        week: data ? data.pair.volumeChange.week : 0,
        month: data ? data.pair.volumeChange.month : 0,
      },
    },
    {
      id: 3,
      title: 'Transactions (24h):',
      value: data ? data.pair.transactionsCount24h : 0,
    },
  ] as InfoTabsDataType[], [currency, data, xtzUsdQuote]);

  const pooledAssets = useMemo(() => [
    {
      id: 1,
      name: data?.pair.token1.symbol,
      value: data ? prettyPrice(
        +new BigNumber(+data?.pair.token1Pool).div(10 ** 6), 2,
      ) : 0,
      logo: data ? getTokenLogo(data.pair.token1.icon!) : null,
    },
    {
      id: 2,
      name: data ? getSlice(data?.pair.token2.symbol!) : '',
      value: data ? prettyPrice(
        +new BigNumber(+data?.pair.token2Pool).div(10 ** (+data.pair.token2.decimals! ?? 6)), 2,
      ) : 0,
      logo: data ? getTokenLogo(data.pair.token2.icon!) : null,
    },
  ] as PooledAssets[], [data]);

  const graphicLiquidityData = useMemo(() => {
    const oldData = plotLiquidity ? [...plotLiquidity.pair.plotLiquidity] : undefined;
    if (oldData && data && data.pair.liquidity) {
      oldData.push({
        time: Date.now() / 1e3,
        value: new BigNumber(data.pair.liquidity).div(1e6).toNumber(),
        xtzUsdQuoteHistorical: xtzUsdQuote,
      });
      return oldData;
    }
    return undefined;
  }, [data, plotLiquidity, xtzUsdQuote]);

  const graphicVolumeData = useMemo(() => {
    const oldData = plotVolume ? [...plotVolume.pair.plotVolume] : undefined;
    if (oldData && data && data.pair.volume24h) {
      oldData.push({
        time: Date.now() / 1e3,
        value: new BigNumber(data.pair.volume24h).div(1e6).toNumber(),
        xtzUsdQuoteHistorical: xtzUsdQuote,
      });
      return oldData;
    }
    return undefined;
  }, [data, plotVolume, xtzUsdQuote]);

  const pairInformation = [
    {
      token1Logo: data ? getTokenLogo(data.pair.token1.icon!) : null,
      token2Logo: data ? getTokenLogo(data.pair.token2.icon!) : null,
      token1Name: loading ? '...' : data?.pair.token1.symbol,
      token2Name: loading ? '...' : data?.pair.token2.symbol,
      pairAddress: data?.pair.id,
      token1Address: data?.pair.token1.id,
      token2Address: data?.pair.token2.id,
      version: data?.pair.version,
    },
  ] as PairInformationDataType[];

  if (error) {
    return (
      <NotFound />
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {`QuipuSwap Statistic - Pair: ${
            loading ? '...' : `
              ${data?.pair.token1.name ?? (data?.pair.token1.symbol ?? 'Unnamed')} (${data?.pair.token1.symbol ?? 'Unnamed'})
              / 
              ${data?.pair.token2.name ?? (data?.pair.token2.symbol ?? 'Unnamed')} (${data?.pair.token2.symbol ?? 'Unnamed'})`
          }`}
        </title>
        <meta
          name="description"
          content={`Get detailed information about a trading pair \
          ${data?.pair.token1.symbol ?? 'Unnamed'} / ${data?.pair.token2.symbol ?? 'Unnamed'}: liquidity, prices, volume, transactions, graphics, etc.`}
        />
      </Helmet>

      <PairHeadSection pair={pairHeadSection} loading={loading} id={data?.pair.id!} />

      <Section title="Pair stats">
        <div className={cx(s.wrapper)}>
          <div className={cx(s.tabs)}>
            {infoTabsData.map(({
              id, title, value, change,
            }) => (
              <InfoTab
                key={id}
                title={title}
                value={value}
                change={change}
                className={cx(s.tab)}
                currency={currency}
                loading={loading}
              />
            ))}

            <InfoTab title="Pooled tokens:" pooledAssets={pooledAssets} loading={loading} />
          </div>

          <div className={cx(s.graph)}>
            <Graphic
              selectItems={selectGraphicsTypes}
              selectActive={currentGraphicType}
              setSelectActive={(el) => handleGraphicChange(el)}
              theme={
                currentGraphicType === SelectGraphicsTypesEnum.Liquidity
                  ? GraphicTypes.Linear
                  : GraphicTypes.Histogram
              }
              data={
                currentGraphicType === SelectGraphicsTypesEnum.Liquidity
                  ? graphicLiquidityData
                  : graphicVolumeData
              }
              loading={
                currentGraphicType === SelectGraphicsTypesEnum.Liquidity
                  ? plotLiquidityLoading
                  : plotVolumeLoading
              }
              tooltipTitle={currentGraphicType}
            />
          </div>
        </div>
      </Section>

      <Section title="Pair information">
        <PairInformationTable data={pairInformation} loading={loading} />
      </Section>

      <Section title="Transactions">
        <TransactionsTable
          data={transactionData}
          scope={TransactionsScopeEnum.PAIR}
          fetchCurrentType={fetchCurrentType}
          pairId={params.pair}
          transactionsByPair
        />
      </Section>
    </>
  );
};

export default PairsDetails;
